import { z } from 'zod';

/**
 * Сводный сметный расчёт (Форма 1) и объектная смета (Форма 3) по НДЦС РК 8.01-08-2022 (СМ-05, СМ-10).
 * В ресурсной модели РК накладных расходов нет: затраты на организацию и управление строительством — глава 8
 * ССР по норме НДЦС РК 8.04-09-2022 (процент от СМР глав 1–7, по виду строительства и температурной зоне);
 * сметная прибыль 5 % от СМР по итогу глав 1–8 (п. 8.2.65.2); непредвиденные — от итога глав 1–8 (п. 8.2.66);
 * НДС — от итога частей I–III по Налоговому кодексу. В локальных и объектных сметах ничего из этого не начисляется.
 */

/** Температурные зоны (приложение Б НДЦС РК 8.04-09-2022) */
export const SSR_ZONES = {
  I: 'I — Жамбылская, Мангистауская, Туркестанская области, Шымкент',
  II: 'II — Алматы, Алматинская, Атырауская, Жетысуская, Кызылординская области',
  III: 'III — Актюбинская, Западно-Казахстанская области',
  IV: 'IV — Астана, Абайская, Акмолинская, ВКО, Карагандинская, Костанайская, Павлодарская, СКО, Улытауская',
} as const;
export type SsrZone = keyof typeof SSR_ZONES;

/**
 * Нормы общеплощадочных затрат (таблица 1 НДЦС РК 8.04-09-2022), % от СМР по итогу глав 1–7, по зонам I–IV.
 * Основные пункты; остальные виды — вручную (процент можно задать своим числом).
 */
export const OVERHEAD_NORMS: Array<{ code: string; name: string; zones: [number, number, number, number] }> =
  [
    { code: '1.1', name: 'Новое строительство: чёрная металлургия', zones: [7.7, 7.9, 8.4, 8.7] },
    { code: '1.2', name: 'Цветная металлургия', zones: [7.0, 7.4, 8.0, 8.5] },
    {
      code: '1.3',
      name: 'Обустройство нефтяных, газовых и газоконденсатных промыслов',
      zones: [8.2, 9.0, 9.8, 10.3],
    },
    { code: '1.4', name: 'Машиностроение и электротехническая промышленность', zones: [7.2, 7.6, 8.3, 8.8] },
    { code: '1.5а', name: 'Шахты', zones: [9.2, 10.1, 10.9, 11.9] },
    { code: '1.5б', name: 'Разрезы, обогатительные фабрики', zones: [8.0, 8.8, 9.7, 10.6] },
    {
      code: '1.6а',
      name: 'Нефтеперерабатывающие и нефтехимические предприятия',
      zones: [8.2, 8.8, 9.7, 10.4],
    },
    { code: '1.6б', name: 'Прочие объекты химической промышленности', zones: [7.5, 7.9, 8.3, 8.8] },
    {
      code: '1.11',
      name: 'Промышленность строительных материалов и стройиндустрия',
      zones: [6.8, 7.3, 7.9, 8.6],
    },
    { code: '1.12', name: 'Лёгкая промышленность', zones: [6.6, 7.0, 7.5, 8.0] },
    { code: '1.13', name: 'Пищевая промышленность', zones: [7.2, 7.4, 8.0, 8.5] },
    { code: '1.16', name: 'Прочие отрасли промышленности', zones: [7.0, 7.5, 8.5, 9.0] },
    { code: '1.17', name: 'Тепловые электростанции', zones: [10.2, 10.8, 12.0, 12.5] },
    { code: '1.19', name: 'Промышленно-отопительные ТЭЦ', zones: [9.8, 10.4, 11.6, 12.1] },
    { code: '1.20', name: 'Самостоятельные котельные', zones: [7.6, 7.9, 9.0, 9.7] },
    { code: '1.21', name: 'Линии электропередачи свыше 35 кВ', zones: [7.7, 7.9, 8.2, 8.4] },
    { code: '1.22', name: 'Трансформаторные подстанции свыше 35 кВ', zones: [8.3, 8.6, 9.1, 9.7] },
    { code: '1.24', name: 'Линии электропередачи и подстанции 0,4–35 кВ', zones: [6.9, 7.2, 7.8, 8.4] },
    { code: '1.25', name: 'Железные дороги', zones: [13.1, 13.5, 13.8, 14.1] },
    { code: '1.29а', name: 'Автомобильные дороги (передвижные АБЗ)', zones: [11.3, 12.0, 12.6, 13.3] },
    { code: '1.29б', name: 'Автомобильные дороги (стационарные заводы)', zones: [9.1, 9.7, 10.4, 11.1] },
    { code: '1.35а', name: 'Аэродромы', zones: [9.4, 9.9, 11.5, 11.7] },
    {
      code: '1.39а',
      name: 'Магистральные водоснабжение и канализация вне городов',
      zones: [7.2, 7.5, 8.6, 9.3],
    },
    {
      code: '1.39б',
      name: 'Площадочные объекты газо- и нефтепроводов (КС, НС, ГРС)',
      zones: [11.5, 11.7, 12.5, 12.9],
    },
    { code: '1.39в', name: 'Линейная часть магистральных трубопроводов', zones: [6.8, 7.2, 8.3, 8.9] },
    { code: '1.39г', name: 'Магистральные тепловые сети', zones: [6.5, 6.9, 8.0, 8.6] },
    { code: '1.40', name: 'Жилые дома без наружных сетей', zones: [5.4, 5.6, 5.9, 6.3] },
    { code: '1.41', name: 'Жилые дома с сетями и благоустройством', zones: [5.6, 5.9, 6.5, 7.0] },
    { code: '1.42', name: 'Микрорайоны', zones: [5.7, 6.0, 6.6, 7.1] },
    { code: '1.43', name: 'Благоустройство городов', zones: [6.0, 6.7, 8.2, 9.2] },
    {
      code: '1.44',
      name: 'Школы, детсады, административные, лечебные и другие общественные здания',
      zones: [6.2, 6.6, 6.9, 7.3],
    },
    { code: '1.45', name: 'Водоснабжение и канализация городов (комплекс)', zones: [7.1, 7.5, 8.3, 8.8] },
    { code: '1.46', name: 'Наружные сети в черте города (линейная часть)', zones: [5.9, 6.3, 7.0, 7.5] },
    { code: '1.47', name: 'Сельскохозяйственное строительство', zones: [7.7, 8.1, 8.7, 9.4] },
    { code: '1.48', name: 'Водохозяйственное строительство', zones: [9.5, 10.3, 11.7, 12.5] },
    { code: '1.51', name: 'Сети и сооружения связи', zones: [8.3, 8.7, 9.2, 9.6] },
    { code: '1.53', name: 'Очистные сооружения по самостоятельному проекту', zones: [7.7, 8.3, 9.2, 9.8] },
    { code: '1.54', name: 'Прочие здания и сооружения', zones: [6.8, 7.3, 8.2, 8.8] },
    { code: '2.1', name: 'Реконструкция: чёрная металлургия', zones: [6.6, 6.8, 7.4, 7.7] },
    { code: '2.20', name: 'Реконструкция: котельные', zones: [6.4, 6.8, 7.9, 8.5] },
    { code: '2.24', name: 'Реконструкция: ЛЭП и подстанции 0,4–35 кВ', zones: [5.8, 6.2, 6.8, 7.4] },
    { code: '2.35', name: 'Реконструкция: жилые дома', zones: [4.6, 4.8, 5.1, 5.4] },
    { code: '2.39', name: 'Реконструкция: общественные здания', zones: [5.3, 5.7, 5.9, 6.3] },
    { code: '2.40', name: 'Реконструкция: водоснабжение и канализация городов', zones: [6.0, 6.4, 7.2, 7.7] },
    { code: '2.41', name: 'Реконструкция: сети в черте города', zones: [5.0, 5.3, 6.0, 6.5] },
    { code: '2.48', name: 'Реконструкция: очистные сооружения', zones: [6.8, 7.3, 8.2, 8.8] },
    { code: '2.49', name: 'Реконструкция: прочие здания и сооружения', zones: [6.1, 6.6, 7.5, 8.1] },
    { code: '3.1', name: 'Снос зданий и сооружений по самостоятельной ПСД', zones: [2.8, 3.1, 3.2, 3.5] },
  ];

export function overheadNorm(code: string, zone: SsrZone): number | null {
  const row = OVERHEAD_NORMS.find((r) => r.code === code);
  if (!row) return null;
  return row.zones[['I', 'II', 'III', 'IV'].indexOf(zone)] ?? null;
}

/** Главы части II ССР (п. 8.2.56 НДЦС РК 8.01-08-2022) */
export const SSR_CHAPTERS: Record<string, string> = {
  '1': 'Подготовка территории строительства',
  '2': 'Основные объекты строительства',
  '3': 'Объекты подсобного и обслуживающего назначения',
  '4': 'Объекты энергетического хозяйства',
  '5': 'Объекты транспортного хозяйства и связи',
  '6': 'Наружные сети и сооружения водоснабжения, канализации, теплоснабжения и газоснабжения',
  '7': 'Благоустройство и озеленение территории',
};

/** Непредвиденные работы и затраты (п. 8.2.66): стадия и назначение объекта */
export const CONTINGENCY_KINDS = {
  heavy: 'Индивидуальные проекты особо сложных объектов (ГОК, промыслы, ГЭС, мосты, метро, химкомбинаты)',
  industry: 'Промышленность, сельское хозяйство, транспорт, связь, коммунальное назначение, инженерные сети',
  public: 'Общественные здания',
  housing: 'Жилые дома',
  typical_industry: 'Типовые проекты производственных объектов',
  typical_public: 'Типовые проекты общественных и жилых зданий',
  demolition: 'Снос (постутилизация)',
} as const;
export type ContingencyKind = keyof typeof CONTINGENCY_KINDS;

export function contingencyNorm(stage: 'p' | 'rp', kind: ContingencyKind): number {
  const p: Record<ContingencyKind, number> = {
    heavy: 7,
    industry: 5,
    public: 5,
    housing: 3,
    typical_industry: 3,
    typical_public: 2,
    demolition: 2,
  };
  const rp: Record<ContingencyKind, number> = {
    heavy: 3.5,
    industry: 3,
    public: 5,
    housing: 3,
    typical_industry: 3,
    typical_public: 2,
    demolition: 2,
  };
  return (stage === 'p' ? p : rp)[kind];
}

/** Ставка НДС по дате уровня цен: 12 % до конца 2025 года, 16 % с 01.01.2026 (проверять по Налоговому кодексу) */
export function vatRateAt(date: string | null | undefined): number {
  if (!date) return 16;
  return date < '2026-01-01' ? 12 : 16;
}

const money = z.number().min(0).max(1e13);

/** Строка части I (проектирование), части III (инжиниринг) или расчётная строка главы 8 */
export const SsrLine = z.object({
  item: z.string().trim().max(20).optional(),
  name: z.string().trim().min(1).max(300),
  basis: z.string().trim().max(300).optional(),
  /** Сумма в графу 6 «прочие»; для главы 8 — amountSmr в графу 4 */
  amount: money.default(0),
  amountSmr: money.optional(),
  /** Процент вместо суммы: от итога части II (часть III) */
  pct: z.number().min(0).max(100).optional(),
  year: z.number().int().min(2000).max(2100).optional(),
});
export type SsrLine = z.infer<typeof SsrLine>;

export const SsrQuarter = z.object({
  label: z.string().trim().min(1).max(40),
  share: z.number().min(0).max(100),
  index: z.number().min(0.1).max(10),
});

export const SsrParams = z.object({
  customer: z.string().trim().max(300).nullable().optional(),
  approvalRef: z.string().trim().max(300).nullable().optional(),
  stage: z.enum(['p', 'rp']).default('rp'),
  /** Пункт таблицы 1 НДЦС РК 8.04-09-2022 и температурная зона — либо свой процент */
  overheadCode: z.string().trim().max(10).nullable().optional(),
  zone: z.enum(['I', 'II', 'III', 'IV']).default('II'),
  overheadPct: z.number().min(0).max(50).nullable().optional(),
  profitPct: z.number().min(0).max(20).default(5),
  contingencyKind: z
    .enum(Object.keys(CONTINGENCY_KINDS) as [ContingencyKind, ...ContingencyKind[]])
    .default('industry'),
  contingencyPct: z.number().min(0).max(20).nullable().optional(),
  vatPct: z.number().min(0).max(30).nullable().optional(),
  partI: z.array(SsrLine).max(50).default([]),
  chapter8: z.array(SsrLine).max(50).default([]),
  partIII: z.array(SsrLine).max(50).default([]),
  /** Переходящий объект: доли по кварталам и индексы НДЦС РК 8.04-07 (сумма долей — 100 %) */
  quarters: z.array(SsrQuarter).max(24).default([]),
  signers: z
    .object({
      head: z.string().trim().max(200).optional(),
      gip: z.string().trim().max(200).optional(),
      estimator: z.string().trim().max(200).optional(),
      checker: z.string().trim().max(200).optional(),
    })
    .optional(),
});
export type SsrParams = z.infer<typeof SsrParams>;

export const SsrRow = z.object({
  no: z.string(),
  basis: z.string(),
  name: z.string(),
  smr: z.number().nullable(),
  equipment: z.number().nullable(),
  other: z.number().nullable(),
  total: z.number().nullable(),
  /** part — заголовок части, chapter — заголовок главы, line — строка, subtotal — итог, total — главный итог */
  kind: z.enum(['part', 'chapter', 'line', 'subtotal', 'total', 'note']),
});
export type SsrRow = z.infer<typeof SsrRow>;

export const SsrView = z.object({
  estimateId: z.uuid(),
  params: SsrParams,
  overheadPct: z.number(),
  contingencyPct: z.number(),
  vatPct: z.number(),
  rows: z.array(SsrRow),
  totals: z.object({
    chapters17: z.number(),
    chapters18: z.number(),
    partII: z.number(),
    partIIForecast: z.number().nullable(),
    partI: z.number(),
    partIII: z.number(),
    beforeVat: z.number(),
    vat: z.number(),
    total: z.number(),
  }),
  warnings: z.array(z.string()),
});
export type SsrView = z.infer<typeof SsrView>;

/** Строка объектной сметы (Форма 3): одна локальная смета */
export const ObjectEstimateRow = z.object({
  no: z.number().int(),
  code: z.string(),
  name: z.string(),
  smr: z.number(),
  equipment: z.number(),
  other: z.number(),
  total: z.number(),
  laborHours: z.number(),
  wages: z.number(),
  estimateId: z.uuid().nullable(),
});
export type ObjectEstimateRow = z.infer<typeof ObjectEstimateRow>;

export const ObjectEstimateView = z.object({
  estimateId: z.uuid(),
  rows: z.array(ObjectEstimateRow),
  totals: z.object({
    smr: z.number(),
    equipment: z.number(),
    other: z.number(),
    total: z.number(),
    laborHours: z.number(),
    wages: z.number(),
  }),
  warnings: z.array(z.string()),
});
export type ObjectEstimateView = z.infer<typeof ObjectEstimateView>;
